import { DateISO } from '@agro-club/agroclub-shared'

import { Bid, BidPrice, Season } from 'modules/domain/bid/types'
import { Company, CompanyShort, FactoringStatus } from 'modules/domain/company/types'
import { Contract } from 'modules/domain/specification/types'
import { Address, Product, RespFile } from 'modules/domain/types'
import { BrokerType, User } from 'modules/domain/user/types'
import { ContractStatuses } from 'views/pages/Contracts/types'
import { RegionalWorkType } from 'views/pages/Deal/DealDetail/types'
import { ProductPurpose } from '../documentRequest/types'
import { SamplesProgressStatuses } from 'views/pages/Samples/types'

export enum DealType {
  trade = 'trade',
  export = 'export',
  cpt = 'cpt',
  warehouse = 'warehouse',
  broker = 'broker',
  railway = 'railway',
}

export enum MarginLevel {
  unknown = 'unknown',
  low = 'low',
  high = 'high',
}

export enum ClosedReason {
  price_ss = 'price_ss',
  price_zz = 'price_zz',
  logistics = 'logistics',
  slow_documents = 'slow_documents',
  no_documents = 'no_documents',
  quality = 'quality',
  small_margin = 'small_margin',
  outer_factors = 'outer_factors',
  better_matches = 'better_matches',
  security_check = 'security_check',
  not_actual_bid = 'not_actual_bid',
  refused_to_sell = 'refused_to_sell',
  payment = 'payment',
}

export enum WhoResponded {
  purchase = 'purchase',
  sale = 'sale',
}

export enum DealSource {
  response = 'response',
  matching = 'matching',
  bid_response = 'bid_response',
  deal_response = 'deal_response',
}

export enum HighlightPrice {
  cpt = 'cpt',
  exw = 'exw',
}

export type Executor = {
  id: string
  full_name: string
  is_active: boolean
  phone?: string
  work_type: RegionalWorkType
}

export type Executors = {
  user_coordinator: Executor
  deal_coordinator: Executor
  shipment_coordinator: Executor
  logistician: Executor
  logisticians: Executor[]
  regionals: Executor[]
  regional?: Executor
  trader: Executor
  ignore_quality?: boolean
}

type AddressShort = {
  id: string
  latitude: number
  longitude: number
}

export type CompanyInDeal = {
  id: string
  name: string
  has_shipped_deals: boolean
  factoring_seller_status?: FactoringStatus
  factoring_customer_status?: FactoringStatus
}

export type ListDealBid = {
  address: AddressShort
  company: CompanyInDeal
  id: string
  owner_has_shipped_deals?: boolean
  owner_is_significant?: boolean
  owner_is_verified_by_coordinator?: boolean
  owner_is_verified_by_kyc?: boolean
  owner_is_verified_by_security?: boolean
  owner_is_verified?: boolean
  owner: User
  parent_id?: string
  price_type: BidPrice
  price: number
}

export type Parameter = {
  id: number
  name: string
  units?: string
}

export type DealContractParams = {
  id: number
  parameter: Parameter
  parameter_value: string
}

export type ValidationRecord = {
  text: string
  missing?: boolean
}

export type DealBroker = {
  id: string
  full_name: string
  is_active: boolean
  phone?: string
}

export type BrokerData = {
  broker: DealBroker
  commission: number
  broker_type: BrokerType
}

export type DeliverySide = 'seller' | 'purchaser' | 'agroclub'

export type DealContract = {
  approved_by_lawyers: boolean
  assignee: Executor
  available_statuses: string[]
  conformity_declaration_type: DealContractType
  contract_type: DealContractType
  deal: string
  dispatching_date: string
  email: string
  farmer_documents_received: boolean
  has_vat: boolean
  id: string
  parameters_comment: string
  parameters: DealContractParams[]
  payment_terms: string
  phone: string
  price: string
  product_purpose: ProductPurpose
  product: Product
  quantity: string | number
  season: string
  seller_company: CompanyShort
  signed_by_agroclub: boolean
  status: ContractStatuses
  transfer_address: string
}
export enum LogisticsRateSource {
  routes = 'routes',
  rates = 'rates',
}

export enum ApprovalStatus {
  NOT_APPROVED = 'not_approved',
  APPROVED_BY_LOGISTICIANS = 'approved_by_logistician',
  APPROVED_BY_COMMERCIAL_DIRECTOR = 'approved_by_commercial_director',
  APPROVED_BY_EXECUTIVE_DIRECTOR = 'approved_by_executive_director',
}

const statusOrder = [
  ApprovalStatus.NOT_APPROVED,
  ApprovalStatus.APPROVED_BY_LOGISTICIANS,
  ApprovalStatus.APPROVED_BY_COMMERCIAL_DIRECTOR,
  ApprovalStatus.APPROVED_BY_EXECUTIVE_DIRECTOR,
]

export const isApprovalStatusAfter = (status: ApprovalStatus, compareStatus: ApprovalStatus) => {
  return statusOrder.indexOf(status) >= statusOrder.indexOf(compareStatus)
}

export type DealDocumentsRequest = {
  id: string
  expected_complete_date: DateISO
  received_ds: boolean
  received_conclusion: boolean
  received_vet: boolean
  received_sdiz: boolean
  product_purpose: ProductPurpose
  request_ds: boolean
  request_quarantine: boolean
  request_sdiz: boolean
  request_vet: boolean
} | null

export type DealTripCountNorm = {
  id: string
  loading_count: number
  unloading_count: number
  use_one_loading_value: boolean
  use_one_unloading_value: boolean
} | null

//TODO: чекнуть DealBase и раскидать ключи которые не входят в общие(деталка и список). Когда окончательно слезим со сделок в us
/* List and detail common types */
type DealBase = {
  actual_contribution_margin_rate: number | null
  actual_contribution_margin: number | null
  actual_margin_per_kg: number
  actual_margin_percent: number
  actual_margin: number
  approval_status: ApprovalStatus
  available_statuses: string[]
  can_move_next_status: boolean
  can_request_documents: boolean
  closed_at: string | null
  closed_reason: ClosedReason | null
  commission?: number
  created?: string
  deal_type: DealType
  delivered_quantity?: number
  delivery_side: DeliverySide
  distance: number
  documents_request: DealDocumentsRequest
  end_date: string | null
  executors: Executors | null
  export_contract_id: string | null
  export_contract_price: number
  fixed_margin_by_export_contract: boolean
  fixed_margin?: number
  gmv?: number
  has_commission_trips: boolean
  has_instructions: boolean
  has_sale_contract_request: boolean
  has_samples: boolean
  highlight_price: HighlightPrice | null
  id: string
  is_approved_by_commercial_director: boolean
  is_position: boolean
  is_private: boolean
  loaded_quantity?: number
  logistics_costs: number
  logistics_rate_source: LogisticsRateSource | null
  logistics_request_id: number | null
  margin_confirmed: boolean
  margin_on_way: number
  margin_per_kg: number
  margin_percent: number
  margin: number
  mark_documents_request: boolean
  mark_purchase_specification: boolean
  modified?: string
  net_margin: number
  non_operating_costs: number
  paid_sum: number
  parameterized_distance: number
  payment_delay_days: number
  price_carrier: number
  price_cpt: number
  price_exw: number
  price_logistics_bag_per_km: number
  price_logistics_per_bag?: number
  price_logistics_per_ton?: number
  price_logistics_ton_per_km: number
  price_logistics: number
  price_logistics_with_overload: number
  railway_price_logistics: number | null
  price_with_logistics_per_ton: number
  price_with_logistics?: number
  product: Product
  purchase_costs: number
  purchase_price: number
  purchase_specification: PurchaseSpecification
  quantity_paid_for: number
  quantity: number
  revenue: number
  sale_contract_request: DealContract
  sale_price: number
  season?: Season
  source: DealSource
  specifications_for_purchase: SpecificationsForPurchase[]
  start_date: string | null
  status_label?: string
  status: DealStatusTypes
  target_margin_percent: number
  tax_from_logistics_price?: number
  trip_count_norm: DealTripCountNorm
  who_responded: WhoResponded | null
  without_documents_request: boolean
  without_purchase_specification: boolean
  days_since_latest_status_change?: number
  is_approved_by_logistician: boolean
  //Hints
  margin_percent_formula: string
  margin_percent_tooltip: string
  margin_percent_is_acceptable: boolean
  has_filled_unload_address_params: boolean
}

/* Deal detail */
export type Deal = DealBase & {
  actual_contribution_margin_per_unit: number
  actual_credit_cost_per_unit: number
  actual_credit_costs: number
  actual_credit_rate: number
  actual_price_logistics: number
  actual_purchase_price: number
  actual_quantity: number
  actual_sale_price: number
  actual_turnover_days: number
  deal_signed_data: {
    id: number
    affectable_turnover_days: number | null
    price_logistics: number | null
    price_logistics_with_overload: number | null
    railway_price_logistics: number | null
    purchase_price: number | null
    quantity: number | null
    sale_price: number | null
    turnover_days: number | null
  } | null
  approved_by_financial_director: boolean
  contribution_margin_per_unit: number
  contribution_margin_rate: number
  contribution_margin: number
  cost_per_unit: number
  credit_costs: number
  credit_rate: number
  customer_actual_turnover_days?: number
  customer_actual_turnover_sync_date?: string
  customer_broker: BrokerData | null
  daily_cost_per_unit: number
  daily_cost: number
  estimated_seller_basis_delivered: number
  estimated_seller_basis: number
  financial_director_sign_available: boolean
  has_warehouse_movements: boolean
  next_status_validation: Record<string, ValidationRecord>
  planned_turnover_days: number
  purchase_bid: Bid
  sale_bid: Bid
  seller_actual_turnover_days?: number
  seller_actual_turnover_sync_date?: string
  seller_broker: BrokerData | null
  turnover_days: number

  payment_delay_days: number | null
  credit_limit: number | null
  credit_limit_comment: string | null
  debt: number | null
  remaining_signed_margin: number | null
  available_credit_limit: number | null

  is_using_overload: boolean
  seller_overload?: boolean
  customer_overload?: boolean
  is_signing_requested: boolean
  logistics_request_sent_to_local_bot_date: string
  deal_railway_data?: {
    all_costs: number | null
    farmer_transit_costs: number | null
    logistics_price: number | null
    rate: number | null
  }

  //Hints
  actual_contribution_margin_formula: string
  actual_contribution_margin_per_unit_formula: string
  actual_contribution_margin_per_unit_tooltip: string
  actual_contribution_margin_rate_formula: string
  actual_contribution_margin_rate_tooltip: string
  actual_contribution_margin_tooltip: string
  actual_credit_cost_per_unit_formula: string
  actual_credit_cost_per_unit_tooltip: string
  actual_credit_costs_formula: string
  actual_credit_costs_tooltip: string
  actual_credit_rate_formula: string
  actual_credit_rate_tooltip: string
  contribution_margin_formula: string
  contribution_margin_per_unit_formula: string
  contribution_margin_per_unit_tooltip: string
  contribution_margin_rate_formula: string
  contribution_margin_rate_tooltip: string
  contribution_margin_tooltip: string
  cost_per_unit_formula: string
  cost_per_unit_tooltip: string
  credit_costs_formula: string
  credit_costs_tooltip: string
  credit_rate_formula: string
  credit_rate_tooltip: string
  estimated_seller_basis_delivered_formula: string
  estimated_seller_basis_delivered_tooltip: string
  estimated_seller_basis_formula: string
  estimated_seller_basis_tooltip: string
  gmv_formula: string
  gmv_tooltip: string
  margin_formula: string
  margin_per_kg_formula: string
  margin_per_kg_tooltip: string
  margin_tooltip: string
  price_logistics_formula: string
  price_logistics_ton_per_km_formula: string
  price_logistics_ton_per_km_tooltip: string
  price_logistics_tooltip: string
}

export type ListDeal = DealBase & {
  sale_bid: ListDealBid
  purchase_bid: ListDealBid
  margin_remaining: number
  margin_remaining_percent: number
  actual_turnover_days_are_filled: boolean
  sample_request_status?: SamplesProgressStatuses
  margin_on_contract_signed: number | null
  margin_percent_on_contract_signed: number | null
  is_for_warehouse: boolean

  //Hints
  margin_remaining_formula: string
  margin_remaining_tooltip: string
  margin_on_way_formula: string
  margin_on_way_tooltip: string
  margin_on_contract_signed_formula: string
  margin_on_contract_signed_tooltip: string
  margin_percent_on_contract_signed_formula: string
  margin_percent_on_contract_signed_tooltip: string
  actual_contribution_margin_formula: string
  actual_contribution_margin_tooltip: string
}

export type DealDTO = {
  quantity: number
  price_logistics: number
  distance: number
  deal_type: DealType
  sale_price: number
  purchase_price: number
  /** @deprecated */
  price_exw: number
  /** @deprecated */
  price_cpt: number
  paid_sum: number
  fixed_margin?: number
}

export type DealShipment = {
  id: string
  delivered_at: string
  quantity: number
  price_exw: number
  price_cpt: number
  price_logistics: number
  gmv: number
  margin: number
  margin_percent: number
  lost_amount: number
  timeout_amount: number
}

export enum DealCondition {
  open = 'open',
  closed = 'closed',
}

export type DealsBidDto = {
  quantity: number
  price: number
}

export type SpecificationsForPurchase = {
  address: Address
  company: Company
  contract: Contract
  deadline: string
  id: string
  number: string
  owner: User
  price: number
  product: Product
  quantity_left: number
  quantity_shipped: number
  quantity: number
  signed_date: string
  status: string
}

export type PurchaseSpecification = {
  contract?: string | null
  id: string
  number: string
  signed_date: string
  status: string
}

export type DealShipmentForm = Omit<
  DealShipment,
  'id' | 'gmv' | 'margin' | 'margin_percent' | 'timeout_amount' | 'lost_amount'
>

export enum DealStatusTypes {
  DRAFT = 'draft',
  NEW = 'new',
  IN_PROGRESS = 'in_progress',
  CUSTOMER_NEGOTIATIONS = 'customer_negotiations',
  AGREED = 'agreed',
  CONTRACT_SIGNED = 'contract_signed',
  PAYMENT_SHIPMENT = 'payment_shipment',
  PICK_UP_COMPLETE = 'pick_up_complete',
  SHIPMENT_COMPLETE = 'shipment_complete',
  DOCUMENTS_ACCEPTED = 'documents_accepted',
  PAYMENT_COMPLETE = 'payment_complete',
  EXPECTED_MARGIN = 'expected_margin',
  CONFIRMED_BATCH = 'confirmed_batch',
  BEST_BATCH = 'best_batch',
}

export const EditableDealStatuses = [
  DealStatusTypes.NEW,
  DealStatusTypes.IN_PROGRESS,
  DealStatusTypes.AGREED,
  DealStatusTypes.CONTRACT_SIGNED,
]

export type DealStatus = {
  slug: DealStatusTypes
  label: string
}

export type Margins = {
  documents_accepted: number
  shipment_complete: number
  payment_shipment: number
  payment_complete: number
  contract_signed: number
  expected_margin: number
  in_progress: number
  agreed: number
  new: number
}
export type MarginsforDeals = {
  margins: Margins
}

export type RuMarginsforDeals = {
  weighted_average_margins: Margins
  margins: Margins
  margins_no_credit: Margins
  weighted_average_margins_no_credit: Margins
}

export type SellerSpec = {
  id: string
  price: string
  files?: RespFile[]
  loading_start_date: string | null
  loading_end_date: string | null
}

export enum DealContractType {
  agroclub = 'agroclub',
  farmer = 'farmer',
}

export enum FarmerLoadWorkTypes {
  sample = 'sample',
  loading = 'loading',
  documents = 'documents',
}
export enum ShipLoadWorkTypes {
  sample = 'sample',
  unloading = 'unloading',
  ship_loading = 'ship_loading',
}

type LoadBase = {
  id: string
  regional: Executor
  quantity: number
  load_date: DateISO
}

export type ShipLoad = LoadBase & {
  work_type: ShipLoadWorkTypes
}
export type FarmerLoad = LoadBase & {
  work_type: FarmerLoadWorkTypes
}

export enum DealPeriodsTypes {
  loading = 'loading',
  delivery = 'delivery',
}

export type DealPeriod = {
  id: number
  start_date: string
  end_date: string
  period_type: DealPeriodsTypes
  deal: number
  is_editable: boolean
}
